import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, getDocs, orderBy, query, where, updateDoc, doc, increment, serverTimestamp } from 'firebase/firestore';
import { Container, Card, Button, Row, Col, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [likedVideos, setLikedVideos] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load liked videos from localStorage
    const savedLikes = JSON.parse(localStorage.getItem('likedVideos') || '{}');
    setLikedVideos(savedLikes);
  }, []);

  const translations = {
    'Videos': 'الفيديوهات',
    'Latest Video Reports': 'أحدث التقارير المصورة',
    'Watch Video': 'مشاهدة الفيديو',
    'Read More': 'اقرأ المزيد',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'No videos found': 'لم يتم العثور على فيديوهات',
    'Error loading videos': 'خطأ في تحميل الفيديوهات',
    'Loading videos...': 'جاري تحميل الفيديوهات...',
    'By': 'بواسطة',
    'Published on': 'نشر في',
    'Field Reports': 'تقارير ميدانية',
    'Documentaries': 'أفلام وثائقية',
    'Interviews': 'مقابلات',
    'Training': 'تدريب',
    'Statements': 'بيانات',
    'Close': 'إغلاق',
    'Duration': 'المدة',
    'Like': 'أعجبني',
    'Liked': 'معجب به',
    'You already liked this video': 'لقد أعجبت بهذا الفيديو مسبقاً',
    'Thank you for your like!': 'شكراً لإعجابك!'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '3rem 2rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(220, 53, 69, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#1a1a1a',
      padding: '160px 20px 80px',
      background: `linear-gradient(135deg, 
        rgba(220, 53, 69, 0.05) 0%, 
        rgba(220, 53, 69, 0.1) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #dc3545 0%, #1a1a1a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 5px 15px rgba(220, 53, 69, 0.2)',
      letterSpacing: '-1px',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: '1.3rem',
      marginBottom: '2rem',
      color: '#dc3545',
      fontWeight: '500',
      maxWidth: '800px',
      margin: '0 auto 2rem',
      lineHeight: '1.5'
    },
    card: {
      backgroundColor: '#ffffff',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '15px',
      transition: 'all 0.3s ease',
      overflow: 'hidden'
    },
    cardHover: {
      transform: 'translateY(-8px) scale(1.02)',
      boxShadow: '0 15px 30px rgba(220, 53, 69, 0.15)',
      borderColor: '#dc3545'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(220, 53, 69, 0.1)',
      borderRadius: '50%'
    },
    likeButton: {
      backgroundColor: 'transparent',
      border: '2px solid #dc3545',
      color: '#dc3545',
      borderRadius: '20px',
      padding: '5px 15px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease'
    },
    likeButtonLiked: {
      backgroundColor: '#dc3545',
      border: '2px solid #dc3545',
      color: 'white'
    }
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!db1) {
          throw new Error('Firebase is not properly initialized.');
        }
        
        const videosQuery = query(
          collection(db1, 'content'),
          where('type', '==', 'video'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(videosQuery);
        
        const videosList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          videosList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            views: data.views || 0,
            likes: data.likes || 0
          });
        });

        setVideos(videosList);
        
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError(`${translateText('Error loading videos')}: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getVideoTypeBadge = (category) => {
    const types = {
      'field_report': { text: translateText('Field Reports'), color: '#dc3545' },
      'documentary': { text: translateText('Documentaries'), color: '#28a745' },
      'interview': { text: translateText('Interviews'), color: '#007bff' },
      'training': { text: translateText('Training'), color: '#6f42c1' },
      'statement': { text: translateText('Statements'), color: '#fd7e14' }
    };
    return types[category] || { text: category, color: '#6c757d' };
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setShowModal(true);
  };

  const handleLike = async (videoId, videoToken) => {
    if (likedVideos[videoToken]) {
      alert(translateText('You already liked this video'));
      return;
    }

    try {
      // Update in Firebase
      await updateDoc(doc(db1, 'content', videoId), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      // Update local state
      setVideos(prev => prev.map(video => 
        video.id === videoId 
          ? { ...video, likes: (video.likes || 0) + 1 }
          : video
      ));

      // Update liked videos
      const newLikedVideos = { ...likedVideos, [videoToken]: true };
      setLikedVideos(newLikedVideos);
      localStorage.setItem('likedVideos', JSON.stringify(newLikedVideos));

      alert(translateText('Thank you for your like!'));

    } catch (err) {
      console.error('Error updating likes:', err);
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const isYouTubeUrl = (url) => {
    return url?.includes('youtube.com') || url?.includes('youtu.be');
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return url;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#dc3545' }} />
            <p className="mt-3" style={{ color: '#dc3545' }}>
              {translateText('Loading videos...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.container}>
        {/* Floating Background Shapes */}
        <div style={styles.floatingShapes}>
          <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(220, 53, 69, 0.05)'}}></div>
          <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(220, 53, 69, 0.08)'}}></div>
          <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(220, 53, 69, 0.03)'}}></div>
        </div>

        {/* Language Toggle Button */}
        <div style={styles.languageToggle}>
          <button 
            onClick={() => setIsArabic(!isArabic)}
            style={styles.toggleButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
              e.target.style.boxShadow = '0 8px 20px rgba(26, 26, 26, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#dc3545';
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
            }}
          >
            {isArabic ? 'ENGLISH' : 'العربية'}
          </button>
        </div>

        {/* Header */}
        <header style={styles.header}>
          <h1 style={styles.title}>{translateText('Videos')}</h1>
          <p style={styles.subtitle}>
            {translateText('Latest Video Reports')}
          </p>
        </header>

        {/* Main Content */}
        <div style={styles.content}>
          {error && (
            <Alert variant="danger" className="mb-4">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            </Alert>
          )}

          {videos.length === 0 && !error ? (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', color: '#dee2e6', marginBottom: '1rem' }}>
                🎥
              </div>
              <h4 style={{ color: '#666' }}>{translateText('No videos found')}</h4>
              <p className="text-muted">Check back later for new video content</p>
            </div>
          ) : (
            <Row>
              {videos.map((video, index) => {
                const badgeInfo = getVideoTypeBadge(video.category);
                const isLiked = likedVideos[video.token];
                const embedUrl = getYouTubeEmbedUrl(video.videoUrl);
                
                return (
                  <Col key={video.id} lg={4} md={6} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm" 
                      style={{ 
                        ...styles.card,
                        ...(hoveredCard === index ? styles.cardHover : {})
                      }}
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <div className="position-relative">
                        <Card.Img 
                          variant="top" 
                          src={video.featuredImage}
                          style={{ 
                            height: '200px', 
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          onClick={() => openVideoModal(video)}
                        />
                        <div className="position-absolute top-0 start-0 m-2">
                          <Badge style={{ backgroundColor: badgeInfo.color }}>
                            {badgeInfo.text}
                          </Badge>
                        </div>
                        <div className="position-absolute bottom-0 end-0 m-2">
                          <Badge style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                            <i className="bi bi-play-circle-fill me-1"></i>
                            {video.duration || '5:30'}
                          </Badge>
                        </div>
                        <div className="position-absolute top-50 start-50 translate-middle">
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(220, 53, 69, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => openVideoModal(video)}
                          >
                            <i className="bi bi-play-fill text-white fs-4"></i>
                          </div>
                        </div>
                      </div>
                      
                      <Card.Body className="d-flex flex-column">
                        <Card.Title style={{ 
                          color: '#dc3545',
                          fontWeight: '600',
                          lineHeight: '1.4',
                          fontSize: '1.1rem',
                          minHeight: '52px',
                          cursor: 'pointer'
                        }}
                        onClick={() => navigate(`/video/${video.token}`)}
                        >
                          {video.title || 'Untitled Video'}
                        </Card.Title>

                        {(video.excerpt || video.description) && (
                          <Card.Text style={{ 
                            color: '#666',
                            flex: 1,
                            lineHeight: '1.6',
                            fontSize: '0.9rem'
                          }}>
                            {truncateText(video.excerpt || video.description, 100)}
                          </Card.Text>
                        )}

                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small style={{ color: '#666', fontWeight: '500' }}>
                              <i className="bi bi-person me-1"></i>
                              {video.author || 'Al-Majd Media'}
                            </small>
                            <small style={{ color: '#666' }}>
                              <i className="bi bi-calendar me-1"></i>
                              {formatDate(video.createdAt)}
                            </small>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-3 text-muted">
                              <small>
                                <i className="bi bi-eye me-1"></i>
                                {video.views || 0}
                              </small>
                              <small>
                                <i className="bi bi-hand-thumbs-up me-1"></i>
                                {video.likes || 0}
                              </small>
                            </div>
                            <div className="d-flex gap-2">
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                style={{ 
                                  ...styles.likeButton,
                                  ...(isLiked ? styles.likeButtonLiked : {})
                                }}
                                onClick={() => handleLike(video.id, video.token)}
                              >
                                <i className={`bi ${isLiked ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-1`}></i>
                                {isLiked ? translateText('Liked') : translateText('Like')}
                              </Button>
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                style={{ 
                                  color: '#dc3545', 
                                  borderColor: '#dc3545',
                                  borderRadius: '20px',
                                  fontWeight: '500'
                                }}
                                onClick={() => navigate(`/video/${video.token}`)}
                              >
                                {translateText('Read More')}
                                <i className="bi bi-arrow-left ms-2" style={isArabic ? {} : { transform: 'scaleX(-1)' }}></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </div>

      {/* Video Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#dc3545' }}>
            {selectedVideo?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVideo && (
            <div className="text-center">
              {selectedVideo.videoUrl && isYouTubeUrl(selectedVideo.videoUrl) ? (
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    src={getYouTubeEmbedUrl(selectedVideo.videoUrl)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="text-center p-5" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '4rem', color: '#dc3545', marginBottom: '1rem' }}>
                    🎥
                  </div>
                  <h4 style={{ color: '#dc3545' }}>{translateText('Watch Video')}</h4>
                  <p style={{ color: '#666' }}>
                    <a href={selectedVideo.videoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#dc3545' }}>
                      {translateText('Click here to watch the video')}
                    </a>
                  </p>
                </div>
              )}
              
              {(selectedVideo.excerpt || selectedVideo.description) && (
                <div className="mt-4 text-start">
                  <p style={{ color: '#666', lineHeight: '1.6' }}>
                    {selectedVideo.excerpt || selectedVideo.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            style={{ 
              borderRadius: '20px', 
              backgroundColor: '#dc3545', 
              borderColor: '#dc3545',
              fontWeight: '600'
            }}
          >
            {translateText('Close')}
          </Button>
          <Button 
            variant="outline-primary"
            onClick={() => selectedVideo && navigate(`/video/${selectedVideo.token}`)}
            style={{ 
              borderRadius: '20px',
              color: '#dc3545',
              borderColor: '#dc3545',
              fontWeight: '600'
            }}
          >
            {translateText('Read More')}
            <i className="bi bi-arrow-left ms-2" style={isArabic ? {} : { transform: 'scaleX(-1)' }}></i>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VideosPage;
